// UrlStorage implementation for tus-js-client, replacing the default localStorage one.
//
// Why not localStorage: one entry is written per file and never removed, so a session with
// 10k files goes past the ~5MB quota. localStorage.setItem then throws synchronously from
// inside tus _createUpload(), and the failure surfaces as "tus: failed to create upload"
// that no retry can recover. The quota is shared with redux-persist, which is why a
// saturated store also logs the user out on reload.
//
// Contract: four promise-returning methods. addUpload resolves with the storage key, and every
// record handed back carries its urlStorageKey, which tus reads to resume.

const DB_NAME = "cloverfield-tus";
// v2 adds the fingerprint index. Records are read straight from IndexedDB, so
// nothing is held in memory: @uppy/tus 3 needed a synchronous-looking lookup and
// lost the race against an async one, Uppy 5 moved the queueing and removed that
// constraint.
const DB_VERSION = 2;
const STORE = "uploads";
const FINGERPRINT_INDEX = "fingerprint";
// 14 days, not 30: nothing removes a record after a successful upload (resuming a
// re-dropped folder depends on it), so this window is the only bound on the store.
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
// The prune is a full pass over the store, so it runs once a day rather than on
// every page load, and never blocks a lookup.
const PRUNE_EVERY_MS = 24 * 60 * 60 * 1000;
const PRUNE_STAMP = "cloverfield-tus:pruned-at";

let addCounter = 0;

const isFresh = (record) =>
  Date.parse(record.creationTime) >= Date.now() - MAX_AGE_MS;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      // Existing users already have the store: createObjectStore would throw.
      const store = request.result.objectStoreNames.contains(STORE)
        ? request.transaction.objectStore(STORE)
        : request.result.createObjectStore(STORE, {
            keyPath: "urlStorageKey",
          });

      if (!store.indexNames.contains(FINGERPRINT_INDEX))
        store.createIndex(FINGERPRINT_INDEX, "fingerprint");
    };

    // A second tab holding the old version open blocks the upgrade. Reject, so
    // `ready` settles: a pending promise would hang every lookup, and tus waits
    // for findPreviousUploads before queuing a file.
    request.onblocked = () =>
      reject(new Error("upgrade blocked by another tab"));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// A storage failure must never reject: tus awaits findPreviousUploads() before queuing a file,
// so a rejection would stall the queue instead of merely losing resumability.
const ready = openDb().catch(() => null);

function transact(mode, run) {
  return ready.then((db) => {
    if (!db) return null;

    return new Promise((resolve, reject) => {
      const store = db.transaction(STORE, mode).objectStore(STORE);
      const request = run(store);
      if (!request) return resolve(null);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  });
}

// Records are kept after a successful upload on purpose, that is what makes a
// re-dropped folder resume instead of re-uploading, so age is what bounds the store.
function pruneIfDue() {
  const last = Number(localStorage.getItem(PRUNE_STAMP)) || 0;
  if (Date.now() - last < PRUNE_EVERY_MS) return;
  localStorage.setItem(PRUNE_STAMP, String(Date.now()));

  ready.then((db) => {
    if (!db) return;

    const cursor = db
      .transaction(STORE, "readwrite")
      .objectStore(STORE)
      .openCursor();

    cursor.onsuccess = () => {
      if (!cursor.result) return;
      // creationTime is written by tus as a Date.toString() value.
      if (!isFresh(cursor.result.value)) cursor.result.delete();
      cursor.result.continue();
    };
  });
}

// Entries written by the previous localStorage-backed storage are dead the moment the storage
// moves: nothing reads them again, but they still count against the quota redux-persist shares.
function clearLegacyLocalStorageUploads() {
  Object.keys(localStorage)
    .filter((key) => key.startsWith("tus::"))
    .forEach((key) => localStorage.removeItem(key));
}

class TusUrlStorage {
  findAllUploads() {
    return transact("readonly", (store) => store.getAll())
      .then((records) => (records ?? []).filter(isFresh))
      .catch(() => []);
  }

  // Expired records are filtered here and not only by the daily prune: resuming
  // from a URL tusd has already dropped would fail the whole upload.
  findUploadsByFingerprint(fingerprint) {
    return transact("readonly", (store) =>
      store.index(FINGERPRINT_INDEX).getAll(fingerprint)
    )
      .then((records) => (records ?? []).filter(isFresh))
      .catch(() => []);
  }

  removeUpload(urlStorageKey) {
    return transact("readwrite", (store) => store.delete(urlStorageKey)).catch(
      () => null
    );
  }

  addUpload(fingerprint, upload) {
    addCounter += 1;
    const record = {
      ...upload,
      fingerprint,
      urlStorageKey: `tus::${fingerprint}::${Date.now()}-${addCounter}`,
    };
    transact("readwrite", (store) => store.put(record)).catch(() => null);

    // Resolved without waiting for the write: tus only needs the key back, and persistence is
    // not on the critical path of the transfer.
    return Promise.resolve(record.urlStorageKey);
  }
}

// Both touch localStorage, which throws a SecurityError when the browser blocks site
// data. Thrown at import time it would break the evaluation of this module and of every
// module importing it, so it fails soft like every other storage access here: the upload
// loses its resumability, not the uploader.
try {
  clearLegacyLocalStorageUploads();
  pruneIfDue();
} catch {
  // Nothing to recover: both calls are housekeeping.
}

const tusUrlStorage = new TusUrlStorage();

export default tusUrlStorage;
